import { EdlinkIdCode } from '@/components/alphanumeric-code';
import { EditableInfoCard } from '@/components/editable-info-card';
import { SubmitProps } from '@/components/editable-info-page';
import { SyncEdlinkSchool } from '../-components/sync-edlink-school';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { RoleBadgeList } from '@/components/users/role-badge';
import { EmptyArray, getSchool, getSchools, getUsers, updateSchool } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import {
  formatScheduleBreak,
  Role,
  schoolStatusSchema,
  UserWithRolesAndProfiles,
  MAX_PAGE_SIZE,
} from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { z } from 'zod';
import { schoolDetailsTabs } from '../-components/tabs';
import { validateRouteRole } from '@/lib/routing';
import { pageSizeSchemaWithMax, paginationRequestSchema, usePagination } from '@/lib/pagination';
import { formatPlainDate, isAirAdmin } from '@airreading/common';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { formatScheduleDates } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { ClassTimesTable } from '../-components/class-times-table';
import { Temporal } from 'temporal-polyfill';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolPage,
  validateSearch: paginationRequestSchema.extend({
    pageSize: pageSizeSchemaWithMax(MAX_PAGE_SIZE, MAX_PAGE_SIZE),
  }),
});

const formSchema = z.object({
  name: z.string(),
  orgId: z.string(),
  schoolStatus: schoolStatusSchema,
  edlinkId: z.string().optional().nullable().default(null),
  canSendSchoolParentReminders: z.boolean().optional().default(false),
  canSendFamilyParentReminders: z.boolean().optional().default(false),
});

const columnHelper = createColumnHelper<UserWithRolesAndProfiles>();
const columns = [
  columnHelper.accessor((row) => row.id, {
    id: 'name',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link to={'/users/$userId'} params={{ userId: getValue() }}>
        {row.original.fullNameOrEmail}
      </Link>
    ),
  }),
  columnHelper.accessor('email', {
    id: 'email',
    header: 'Email',
  }),
  columnHelper.accessor('phone', {
    id: 'phone',
    header: 'Phone',
  }),
  columnHelper.accessor('timeZone', {
    id: 'timezone',
    header: 'Time zone',
  }),
  columnHelper.accessor('userRoles', {
    id: 'roles',
    header: 'Roles',
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <RoleBadgeList userRoles={getValue()} />
        </div>
      );
    },
  }),
];

function SchoolPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load school. Please try again.',
          back: {
            backLink: {
              to: '/orgs',
            },
          },
        };
      }}
    >
      <SchoolDetails />
    </InfoPageSuspenseBoundary>
  );
}

function SchoolDetails() {
  const userRolesToFetch: Role[] = ['school_admin', 'school_contact'];
  const { schoolId } = Route.useParams();
  const { pageIndex, pageSize, onPaginationChange } = usePagination(Route.id);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(
    getSchool(schoolId, {
      filters: { shouldIncludeArchivedSchedules: true },
    }),
  );

  const {
    data: usersResponse,
    isPending: isLoadingUsers,
    error: loadingUserErrors,
    refetch: refetchUsers,
  } = useQuery(
    getUsers({
      pageIndex,
      pageSize,
      filters: {
        schools: [data.id],
        roles: userRolesToFetch,
      },
    }),
  );

  const schedules = data.schedules
    .filter((s) => s.schoolId === data.id)
    .sort((a, b) => Temporal.PlainDate.compare(b.startPlainDate, a.startPlainDate));

  const { mutateAsync } = useMutation({
    mutationFn: updateSchool,
  });

  const onSubmit = ({ formData, disableEditing }: SubmitProps<z.infer<typeof formSchema>>) => {
    return mutateAsync({
      schoolId,
      data: {
        ...formData,
        edlinkId: ((edlinkId) => {
          switch (edlinkId) {
            case null: {
              return null;
            }
            default: {
              if (!edlinkId.trim()) {
                return null;
              }
              return edlinkId;
            }
          }
        })(formData.edlinkId),
      },
    })
      .then(() => {
        toast({
          title: 'School updated successfully',
        });
        disableEditing();
        queryClient.invalidateQueries(getSchools());
        queryClient.invalidateQueries(
          getSchool(schoolId, {
            filters: { shouldIncludeArchivedSchedules: true, shouldIncludeDeletedSchedules: true },
          }),
        );
      })
      .catch(() => {
        toast({
          title: 'School update failed',
        });
      });
  };

  const table = useReactTable({
    data: usersResponse?.users || EmptyArray,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: usersResponse?.total ?? 0,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onPaginationChange,
  });

  return (
    <InfoPage
      headerText={data.name || data.id}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Schools',
            link: {
              to: '/orgs/schools',
            },
          },
        ],
        currentPage: data.name,
      }}
      headerActions={
        <>
          {data.edlinkId && (
            <SyncEdlinkSchool
              schoolId={data.id}
              schoolName={data.name}
              schoolEdlinkId={data.edlinkId}
              trigger={<Button>Sync from Edlink</Button>}
            />
          )}
        </>
      }
      tabs={schoolDetailsTabs({ schoolId: data.id })}
    >
      <>
        <EditableInfoCard
          headerText="Basic information"
          formSchema={formSchema}
          onSubmit={onSubmit}
          defaultValues={{
            name: data.name,
            orgId: data.orgId,
            schoolStatus: data.schoolStatus,
            edlinkId: data.edlinkId ?? '',
            canSendSchoolParentReminders: data.canSendSchoolParentReminders ?? false,
            canSendFamilyParentReminders: data.canSendFamilyParentReminders ?? false,
          }}
        >
          {({ form, editing }) => {
            return (
              <>
                <InfoKeyValue
                  label="School name"
                  value={data.name}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormInputField
                        form={form}
                        name="name"
                        label="School name"
                        inputProps={{ placeholder: 'School name' }}
                      />
                    ),
                  }}
                />
                <InfoKeyValue label="District" value={data.org.name} />
                <InfoKeyValue label="Time zone" value={data.org.timeZone || '-'} />
                <InfoKeyValue
                  label="Edlink ID"
                  value={<EdlinkIdCode code={data.edlinkId} />}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormInputField
                        form={form}
                        name="edlinkId"
                        label="Edlink ID"
                        inputProps={{ className: 'font-mono' }}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label={'Status'}
                  value={data.schoolStatus}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormSingleSelect
                        name="schoolStatus"
                        label="Status"
                        form={form}
                        options={schoolStatusSchema.options}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Enable parent reminder email and SMS (1 hr)"
                  value={data.canSendSchoolParentReminders ? 'Yes' : 'No'}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormCheckbox
                        name="canSendSchoolParentReminders"
                        label="Enable parent reminder email and SMS (1 hr)"
                        form={form}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Enable family parent reminder email (1 hr)"
                  value={data.canSendFamilyParentReminders ? 'Yes' : 'No'}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormCheckbox
                        name="canSendFamilyParentReminders"
                        label="Enable family parent reminder email (1 hr)"
                        form={form}
                      />
                    ),
                  }}
                />
              </>
            );
          }}
        </EditableInfoCard>

        <InfoCard headerText="School admins & contacts">
          <EntityTable
            table={table}
            isLoading={isLoadingUsers}
            hasError={!!loadingUserErrors}
            emptyState={'No admins or contacts found'}
            errorState={<TableErrorState refetchAction={refetchUsers} />}
            emptyStateClassName="translate-y-0"
            showPagination
            totalRowCount={usersResponse?.total}
          />
        </InfoCard>

        <InfoCard headerText="Archived Schedules" maxItemsPerRow={1}>
          {schedules.map((schedule) => {
            const { id, name, practicePlainDate, diagnosticPlainDate, scheduleBreaks } = schedule;
            const regularDates = formatScheduleDates(schedule);
            const practiceDate = practicePlainDate ? formatPlainDate(practicePlainDate) : '';
            const diagnosticDate = diagnosticPlainDate ? formatPlainDate(diagnosticPlainDate) : '';

            return (
              <InfoCard key={`schedule-${id}`} headerText={name}>
                <InfoKeyValue label="Regular class dates" value={regularDates} />
                <InfoKeyValue label="Practice date" value={practiceDate} />
                <InfoKeyValue label="Diagnostic date" value={diagnosticDate} />
                <InfoKeyValue
                  label="Break"
                  value={
                    <>
                      {scheduleBreaks.map((scheduleBreak, i) => {
                        return <div key={`schedule-break-${id}-${i}`}>{formatScheduleBreak(scheduleBreak)}</div>;
                      })}
                    </>
                  }
                />
                <div className="w-full col-span-4">
                  <ClassTimesTable
                    scheduleId={id}
                    classTimeTypes={['recurring']}
                    schoolTimeZone={data.org.timeZone}
                    hideColumnsIds={['actions']}
                  />
                </div>
              </InfoCard>
            );
          })}
        </InfoCard>
      </>
    </InfoPage>
  );
}
